import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { Button, Typography } from '@tempmee/react';
import { createEmptyUser, fetchUsers } from '../../../../actions/userActions';
import { createLoadingSelector } from '../../../../apis/selectors';
import history from '../../../../history';
import { getQueryParam, setQueryParams, updateQueryParam } from '../../../../utils';
import { ListPageHeader } from '../../../commonComponents/ListPageHeader';
import { Spinner } from '../../../commonComponents/Spinner';
import { Table } from '../../../commonComponents/Table/Table';
import { userListColumn } from '../../../commonComponents/user-column';
import { filterKeys } from '../utils';
import { CustomHeader } from './CustomHeader';

const ROWS_PER_PAGE = 15;

const initialActions = ['GET_ADMIN_RAM', 'GET_DSO_NAMES'];
const initialLoadingSelector = createLoadingSelector(initialActions);

const actions = ['FETCH_DENTIST', 'FETCH_DSO', 'FETCH_HYGIENIST', 'FETCH_UNFINISHED_USERS'];
const loadingSelector = createLoadingSelector(actions);

const useFilterState = (searchParams) => {
  const initialFilterState = filterKeys.reduce((acc, key) => {
    acc[key] =
      searchParams?.[key] ??
      getQueryParam(key) ??
      (key === 'page' ? 1 : key === 'order' ? 'desc' : '');
    return acc;
  }, {});

  return useState(initialFilterState);
};

export const CustomTable = ({
  title,
  data,
  count,
  searchParams,
  params,
  conditionalRowStyles,
  CustomFilters,
  omitColumns = [],
}) => {
  const [initialPageState, setInitialPageState] = useState(getQueryParam('page'));
  const dispatch = useDispatch();
  const location = useLocation();
  const initialLoading = useSelector((state) => initialLoadingSelector(state));
  const isLoading = useSelector((state) => loadingSelector(state));
  const [filter, setFilter] = useFilterState(searchParams);
  const totalPages = Math.ceil(count / ROWS_PER_PAGE);

  const handleFilterChange = (type, value) => {
    const typeParamValue = getQueryParam(type);
    if (typeParamValue === value) {
      return;
    }

    updateQueryParam(type, value);
    setFilter((prevState) => ({ ...prevState, [type]: value, page: 1 }));
  };

  const handleTableChange = (page) => {
    setFilter((prevState) => ({ ...prevState, page }));
    updateQueryParam('page', page);
  };

  const handleOnSort = (selectedColumn, sortDirection) => {
    const sortObj = {
      page: filter.page,
      sortField: selectedColumn.sortField,
      order: sortDirection,
    };
    setQueryParams(sortObj, filterKeys);
    setFilter((prevState) => ({
      ...prevState,
      ...sortObj,
    }));
  };

  useEffect(() => {
    dispatch(
      fetchUsers({
        ...filter,
        ...params,
      }),
    );
  }, [filter, dispatch, params]);

  useEffect(() => {
    setQueryParams(filter, filterKeys);
    setInitialPageState(filter.page);
  }, [filter, location]);

  const onRowClicked = (row) => {
    if (row.user_type === 'DNT') {
      history.push(`/user/dentist/${row._id}`);
    } else {
      history.push(`/user/hygienist/${row._id}`);
    }
  };

  const getColumn = () => userListColumn();

  if (initialLoading) {
    return <Spinner />;
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        alignItems: 'center',
      }}
    >
      <div style={{ width: '90%' }}>
        <ListPageHeader
          title={title}
          isLoading={isLoading}
          actions={(
            <>
              {params.user_type !== 'HYG' && <AddDNTButton />}
              {params.user_type !== 'DNT' && <AddHYGButton />}
            </>
          )}
          meta={(
            <Typography size="body_large" bold>
              Total | {count !== undefined ? new Intl.NumberFormat().format(count) : ''}
            </Typography>
          )}
        />
        <CustomHeader
          filter={filter}
          handleFilterChange={handleFilterChange}
          CustomFilters={CustomFilters}
        />
        <Table
          filter={filter}
          initialPage={initialPageState}
          columns={getColumn()?.filter((column) => !omitColumns.includes(column.name))}
          data={data}
          noDataComponent="There are no users"
          isLoading={isLoading}
          totalPages={totalPages}
          paginationPerPage={ROWS_PER_PAGE}
          paginationTotalRows={count}
          showTotal={false}
          onSort={handleOnSort}
          onRowClicked={onRowClicked}
          onPageChange={handleTableChange}
          conditionalRowStyles={conditionalRowStyles}
        />
      </div>
    </div>
  );
};

const AddDNTButton = () => {
  const dispatch = useDispatch();

  return (
    <Button onClick={() => dispatch(createEmptyUser('DNT'))}>Create Empty Dentist</Button>
  );
};

const AddHYGButton = () => {
  const dispatch = useDispatch();

  return (
    <Button onClick={() => dispatch(createEmptyUser('HYG'))}>Create Empty Hygienist</Button>
  );
};
